"use client";

import { usePathname } from "next/navigation";
import { ThemeProvider } from "next-themes";
import type { ReactNode } from "react";
import { isThemePinned, THEME_SWITCHER_ENABLED } from "@/lib/theme";

/**
 * next-themes, with the production light pin decided per route.
 *
 * A client component of its own because `usePathname` needs one and
 * `Providers` is not; pulling the whole provider stack across that boundary
 * to read a path would be a larger change than the one being made.
 *
 * The pathname is known during server rendering, so the first paint already
 * carries the right `forcedTheme` and the script next-themes injects sets the
 * class before anything shows. On client navigation the prop changes and
 * next-themes re-applies it (its apply effect depends on `forcedTheme`), so
 * leaving the portal for Framework pins light again without a reload.
 */
export function RouteThemeProvider({
  dsPreview,
  children,
}: {
  /** On the design-system preview allowlist: never pinned, on any route. */
  dsPreview: boolean;
  children: ReactNode;
}) {
  const pathname = usePathname();
  const pinned =
    !THEME_SWITCHER_ENABLED && !dsPreview && isThemePinned(pathname);
  return (
    // attribute="class" writes .dark on <html>, which is what both the DS dark
    // block and globals.css's @custom-variant dark (&:is(.dark *)) key off.
    // defaultTheme="system" + enableSystem means an untouched install follows
    // the OS; an explicit choice persists to localStorage and wins from then on.
    //
    // forcedTheme pins light while the switcher is dormant. It overrides both
    // the stored choice and the OS, so someone on a dark OS — or who toggled
    // while the flag was on — still gets light, rather than a half-converted
    // page. The three ways out of the pin are documented in lib/theme.ts.
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
      forcedTheme={pinned ? "light" : undefined}
    >
      {children}
    </ThemeProvider>
  );
}
